import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { Banner, Button, Skeleton, StatusBadge, useToast, type StatusBadgeTone } from '@immo/ui-kit';
import type { SandboxResetDataKind, SandboxResetRun, SandboxResetRunStatus } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { RequirePermission } from '@/lib/auth/permissions';
import { env } from '@/lib/env';
import { useTranslations } from '@/i18n/use-translations';
import { AlertCircleIcon, ExternalLinkIcon } from '@/components/layout/icons';
import { SandboxResetDialog } from '@/components/sandbox/sandbox-reset-dialog';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { NotFoundPage } from '@/pages/not-found';

const FORBIDDEN = 403;

const STATUS_TONES = {
  QUEUED: 'neutral',
  IN_PROGRESS: 'info',
  SUCCEEDED: 'success',
  FAILED: 'danger',
} as const satisfies Record<SandboxResetRunStatus, StatusBadgeTone>;

type Latest =
  | { readonly kind: 'loading' }
  | {
      readonly kind: 'loaded';
      readonly run: SandboxResetRun | null;
      readonly busy: boolean;
      readonly defaultDataKind: SandboxResetDataKind;
    }
  | { readonly kind: 'unavailable' };

export function SandboxPage() {
  return env.sandbox ? <NotFoundPage /> : <SandboxOverview />;
}

function SandboxOverview() {
  const t = useTranslations('pages.admin.sandbox');

  return (
    <>
      <PageTopbar leading={<h1 className="truncate text-base font-semibold text-text-primary">{t('title')}</h1>} />
      <div className="flex flex-col gap-6 bg-canvas p-4">
        <p className="max-w-prose text-sm text-text-secondary">{t('intro')}</p>
        <RequirePermission permission="backoffice:fleet:command">
          <LatestReset />
        </RequirePermission>
      </div>
    </>
  );
}

function LatestReset() {
  const t = useTranslations('pages.admin.sandbox');
  const navigate = useNavigate();
  const toast = useToast();
  const [latest, setLatest] = useState<Latest>({ kind: 'loading' });
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    let current = true;

    const read = async () => {
      try {
        const result = await apiClient.adminSandbox.getReset();

        if (!current) return;

        if (result.ok) {
          setLatest({
            kind: 'loaded',
            run: result.body.latestRun,
            busy: result.body.busy,
            defaultDataKind: result.body.defaultDataKind,
          });
        } else if (result.status === FORBIDDEN) {
          navigate('/access-denied', { replace: true, state: { reason: 'missing-permission' } });
        } else {
          setLatest({ kind: 'unavailable' });
        }
      } catch {
        if (current) setLatest({ kind: 'unavailable' });
      }
    };

    void read();

    return () => {
      current = false;
    };
  }, [navigate]);

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-text-tertiary">{t('latest-heading')}</h2>
      {latest.kind === 'loading' && <Skeleton className="h-20 w-full max-w-md" />}
      {latest.kind === 'unavailable' && (
        <Banner variant="warning" icon={<AlertCircleIcon />} title={t('status-unavailable')} />
      )}
      {latest.kind === 'loaded' &&
        (latest.run === null ? (
          <p className="text-sm text-text-secondary">{t('empty')}</p>
        ) : (
          <RunRows run={latest.run} />
        ))}
      <div className="flex flex-col gap-2">
        <Button
          variant="primary"
          type="button"
          className="w-fit"
          disabled={latest.kind !== 'loaded' || latest.busy}
          onClick={() => setDialogOpen(true)}
        >
          {t('reset-cta')}
        </Button>
        {latest.kind === 'loaded' && latest.busy && <p className="text-sm text-text-secondary">{t('busy-note')}</p>}
      </div>
      {dialogOpen && latest.kind === 'loaded' && (
        <SandboxResetDialog
          defaultDataKind={latest.defaultDataKind}
          onClose={() => setDialogOpen(false)}
          onStarted={(run) => {
            setDialogOpen(false);
            toast.success(t('started-toast'));
            setLatest({ ...latest, run, busy: true });
          }}
          onRefused={() => setLatest({ ...latest, busy: true })}
        />
      )}
    </section>
  );
}

function RunRows({ run }: { readonly run: SandboxResetRun }) {
  const t = useTranslations('pages.admin.sandbox');
  const intl = useIntl();
  const startedAt = intl.formatDate(run.startedAt, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
  });

  return (
    <div className="flex flex-col gap-3">
      {run.status === 'FAILED' && <Banner variant="danger" icon={<AlertCircleIcon />} title={t('failure-note')} />}
      <p className="text-sm text-text-primary">{t('started', { startedAt })}</p>
      <p className="flex items-center gap-2 text-sm text-text-primary">
        {t('status-label')}
        <StatusBadge label={t(`statuses.${run.status}`)} tone={STATUS_TONES[run.status]} size="sm" />
      </p>
      <a
        href={run.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex w-fit items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
      >
        {t('view-run')}
        <ExternalLinkIcon aria-hidden className="h-3 w-3" />
      </a>
    </div>
  );
}
